import {
  ArrowUpTrayIcon,
  CalculatorIcon,
  ClipboardDocumentListIcon,
  CpuChipIcon,
  CurrencyYenIcon,
  EyeDropperIcon,
  FolderIcon,
  HomeIcon,
  ScaleIcon,
  TableCellsIcon,
} from '@heroicons/react/24/solid';
import { useMatchRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { ReleaseFlagType } from '@/entities/session/model/releaseFlag';
import { useReleaseFlag } from '@/entities/session/model/useReleaseFlag';
import type { NavMenuItem } from '@/shared/ui/layout/nav-menu-items/NavMenuItems';

type ItemDef = NavMenuItem & {
  releaseFlagType?: ReleaseFlagType;
};

export const useRouteMenu = (): NavMenuItem[] => {
  const { t } = useTranslation();
  const matchRoute = useMatchRoute();

  // 各リリースフラグの有効状態を取得（Record で網羅性を強制）
  const releaseFlagEnabledMap: Record<ReleaseFlagType, boolean> = {
    general_chat: useReleaseFlag('general_chat'),
    settlement_statistics_converter: useReleaseFlag('settlement_statistics_converter'),
    memo: useReleaseFlag('memo'),
  };

  // fuzzy マッチで部分一致を許可
  const isHomePage = !!matchRoute({ to: '/home', fuzzy: true });
  const isAiAssistantPage = !!matchRoute({ to: '/ai-chat', fuzzy: true });
  const isProjectsPage = !!matchRoute({ to: '/projects', fuzzy: true });
  const isAssessmentPage = !!matchRoute({ to: '/assessment', fuzzy: true });
  const isDataImportPage = !!matchRoute({ to: '/data-import', fuzzy: true });
  const isSettlementStatisticsConverterPage = !!matchRoute({
    to: '/settlement-statistics-converter',
    fuzzy: true,
  });
  const isDatConversionPage = !!matchRoute({ to: '/dat-conversion', fuzzy: true });
  const isJobPage = !!matchRoute({ to: '/job', fuzzy: true });
  const isPlanUsagePage = !!matchRoute({ to: '/plan-usage', fuzzy: true });
  const isMasterPage = !!matchRoute({ to: '/master', fuzzy: true });

  const currentRoute = (() => {
    if (isHomePage) return 'home';
    if (isAiAssistantPage) return 'ai-assistant';
    if (isJobPage) return 'job';
    if (isAssessmentPage) return 'assessment';
    if (isSettlementStatisticsConverterPage) return 'settlement-statistics-converter';
    if (isDatConversionPage) return 'dat-conversion';
    if (isDataImportPage) return 'data-import';
    if (isPlanUsagePage) return 'plan-usage';
    if (isMasterPage) return 'master';
    if (isProjectsPage) return 'projects';
    return 'home'; // デフォルトは home
  })();

  // ナビゲーションアイテムの定義
  const allItems: ItemDef[] = [
    {
      key: 'home',
      icon: HomeIcon,
      label: t('home.navigation.title'),
      isActive: currentRoute === 'home',
      to: '/home',
      hasBottomSeparator: true,
    },
    {
      key: 'ai-assistant',
      icon: CpuChipIcon,
      label: t('ai.ai-assistant.navigation-title'),
      isActive: currentRoute === 'ai-assistant',
      to: '/ai-chat',
      hasBottomSeparator: true,
      releaseFlagType: 'general_chat',
    },
    {
      key: 'projects',
      sectionHeader: t('budget.section-header'),
      icon: FolderIcon,
      label: t('project.navigation.title'),
      isActive: currentRoute === 'projects',
      to: '/projects',
    },
    {
      key: 'assessment',
      icon: ScaleIcon,
      label: t('assessment.navigation.title'),
      isActive: currentRoute === 'assessment',
      to: '/assessment',
    },
    {
      key: 'settlement-statistics-converter',
      sectionHeader: t('settlement-statistics.section-header'),
      icon: CalculatorIcon,
      label: t('settlement-statistics-converter.navigation.code-assignment'),
      isActive: currentRoute === 'settlement-statistics-converter',
      to: '/settlement-statistics-converter',
      releaseFlagType: 'settlement_statistics_converter',
    },
    {
      key: 'dat-conversion',
      icon: EyeDropperIcon,
      label: t('dat-conversion.navigation.title'),
      isActive: currentRoute === 'dat-conversion',
      to: '/dat-conversion',
      hasBottomSeparator: true,
      releaseFlagType: 'settlement_statistics_converter',
    },
    {
      key: 'data-import',
      icon: ArrowUpTrayIcon,
      label: t('data-import.navigation.title'),
      isActive: currentRoute === 'data-import',
      to: '/data-import',
    },
    {
      key: 'job',
      icon: ClipboardDocumentListIcon,
      label: t('job.navigation.title'),
      isActive: currentRoute === 'job',
      to: '/job',
      hasBottomSeparator: true,
    },
    {
      key: 'master',
      sectionHeader: t('master-management.section-header'),
      icon: TableCellsIcon,
      label: t('master-management.navigation.title'),
      isActive: currentRoute === 'master',
      to: '/master/accounting-types',
    },
    {
      key: 'plan-usage',
      icon: CurrencyYenIcon,
      label: t('plan-usage.navigation.title'),
      isActive: currentRoute === 'plan-usage',
      to: '/plan-usage',
    },
  ];

  // リリースフラグが false の項目はメニューから除外
  return allItems
    .filter((item) => !item.releaseFlagType || releaseFlagEnabledMap[item.releaseFlagType])
    .map(({ releaseFlagType: _releaseFlagType, ...rest }) => rest);
};
